import React, { useState } from "react";
import { Box, Paper, TextField, Button, Stack, Switch, MenuItem, Typography } from "@mui/material";
import { Trash2, Plus } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { automationService } from "../../api/services/automationService";
import { PageHeader, ConfirmDialog } from "../../components/Common";
import DataTable from "../../components/DataTable";

export default function AutomationPage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ name: "", trigger_event: "ticket_created", is_active: true });

  const { data: rules, isLoading } = useQuery({ queryKey: ["automation-rules"], queryFn: automationService.listRules });

  const createMutation = useMutation({
    mutationFn: () => automationService.createRule({ ...form, conditions: [], actions: [] }),
    onSuccess: () => {
      setFormOpen(false);
      setForm({ name: "", trigger_event: "ticket_created", is_active: true });
      enqueueSnackbar("Rule created (edit conditions/actions via API for now)", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["automation-rules"] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }) => automationService.updateRule(id, { is_active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["automation-rules"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => automationService.deleteRule(id),
    onSuccess: () => {
      enqueueSnackbar("Rule deleted", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["automation-rules"] });
      setDeleteTarget(null);
    },
  });

  const columns = [
    { key: "name", label: "Rule", render: (r) => <strong>{r.name}</strong> },
    { key: "trigger_event", label: "Trigger" },
    { key: "conditions", label: "Conditions", render: (r) => `${r.conditions.length} condition(s)` },
    { key: "actions", label: "Actions", render: (r) => `${r.actions.length} action(s)` },
    {
      key: "is_active", label: "Active",
      render: (r) => (
        <Switch checked={r.is_active} onChange={(e) => toggleMutation.mutate({ id: r.id, is_active: e.target.checked })} onClick={(e) => e.stopPropagation()} />
      ),
    },
    {
      key: "delete", label: "", width: 60,
      render: (r) => (
        <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}><Trash2 size={16} /></Button>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Automation Rules"
        subtitle="Round-robin assignment runs automatically for team-assigned tickets; rules below add conditional actions on top"
        action={<Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setFormOpen(!formOpen)}>New Rule</Button>}
      />

      {formOpen && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Stack spacing={2}>
            <TextField label="Rule name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
            <TextField select label="Trigger" value={form.trigger_event} onChange={(e) => setForm({ ...form, trigger_event: e.target.value })}>
              <MenuItem value="ticket_created">Ticket Created</MenuItem>
              <MenuItem value="ticket_updated">Ticket Updated</MenuItem>
            </TextField>
            <Typography variant="caption" color="text.secondary">
              Conditions/actions (e.g. "priority = Critical → add tag urgent") are JSON structures — build them via the API for now; a visual builder is a good next iteration for this screen.
            </Typography>
            <Box>
              <Button variant="contained" disabled={!form.name || createMutation.isPending} onClick={() => createMutation.mutate()}>
                Create Rule
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      <DataTable columns={columns} rows={rules} loading={isLoading} emptyMessage="No automation rules yet" />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete rule?"
        message={`Delete "${deleteTarget?.name}"? This can't be undone.`}
        confirmLabel="Delete" danger
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
      />
    </Box>
  );
}
